import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import Nav from "../components/Nav";
import SEO, { generateArticleSchema } from "../components/SEO";
import { db } from "../firebase";
import {
  getArticleCategoryUrl,
  getResourceCategoryUrl,
} from "../utils/seoPaths";
import {
  filterPublishedArticles,
  getPublicationDate,
  isArticlePublished,
} from "../utils/articlePublishing";

const ArticleView = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  // Tell prerender when the article or not-found state is ready.
  useEffect(() => {
    if (loading) return;
    // Give React a tick to commit <SEO/> tags into the DOM
    const t = setTimeout(() => {
      window.prerenderReady = true;
    }, 60);
    return () => clearTimeout(t);
  }, [loading]);

  useEffect(() => {
    if (typeof window !== "undefined") window.prerenderReady = false;
  }, []);

  const loadArticle = async () => {
    setLoading(true);
    setArticle(null);
    setRelatedArticles([]);
    if (typeof window !== "undefined") window.prerenderReady = false;
    try {
      // First try to find article by slug
      const slugQuery = query(
        collection(db, "articles"),
        where("slug", "==", id)
      );
      const slugSnapshot = await getDocs(slugQuery);

      let articleData = null;

      if (!slugSnapshot.empty) {
        // Found by slug
        const doc = slugSnapshot.docs[0];
        articleData = {
          id: doc.id,
          ...doc.data(),
        };
      } else {
        // Fallback: try to get by document ID (for backward compatibility)
        const docRef = doc(db, "articles", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          articleData = {
            id: docSnap.id,
            ...docSnap.data(),
          };
        }
      }

      if (articleData && isArticlePublished(articleData)) {
        setArticle(articleData);

        // Load related articles from the same category
        const q = query(
          collection(db, "articles"),
          where("category", "==", articleData.category),
          where("date", "<=", getPublicationDate()),
          orderBy("date", "desc")
        );
        const querySnapshot = await getDocs(q);
        const related = [];
        querySnapshot.forEach((doc) => {
          const relatedData = { id: doc.id, ...doc.data() };
          // Exclude current article (check both slug and ID)
          if (
            doc.id !== articleData.id &&
            relatedData.slug !== articleData.slug
          ) {
            related.push(relatedData);
          }
        });
        setRelatedArticles(filterPublishedArticles(related).slice(0, 3));
      }
    } catch (error) {
      console.error("Error loading article:", error);
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "unsung-heroes":
        return "bg-emerald-500";
      case "stories-of-recovery":
        return "bg-purple-500";
      case "blog-posts":
        return "bg-cyan-500";
      default:
        return "bg-gray-500";
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "unsung-heroes":
        return "Unsung Heroes";
      case "stories-of-recovery":
        return "Recovery Stories";
      case "blog-posts":
        return "Blog Posts";
      default:
        return "Article";
    }
  };

  const getFormattedArticleContent = (content = "") => {
    if (typeof document === "undefined") return content;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;

    wrapper.querySelectorAll(".content-image-selected").forEach((el) => {
      el.classList.remove("content-image-selected");
    });

    wrapper.querySelectorAll("img").forEach((image) => {
      image.setAttribute("loading", "lazy");
      image.setAttribute("decoding", "async");
    });

    Array.from(wrapper.childNodes).forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const text = node.textContent.replace(/\u00a0/g, " ").trim();
      if (!text) {
        node.remove();
        return;
      }

      const paragraph = document.createElement("p");
      paragraph.textContent = text;
      node.replaceWith(paragraph);
    });

    wrapper.querySelectorAll("p, div").forEach((el) => {
      const hasMedia = el.querySelector("img, iframe, video, hr");
      const text = el.textContent.replace(/\u00a0/g, " ").trim();
      if (!hasMedia && !text && !el.innerHTML.trim()) {
        el.innerHTML = "<br>";
      }
    });

    return wrapper.innerHTML.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-serif">
        <Nav />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-cyan-400 mb-4">
            Loading Article...
          </h1>
          <p className="text-gray-300">
            Please wait while we fetch the article.
          </p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-serif">
        <SEO
          title="Article Not Found"
          description="The article you are looking for does not exist or has been removed."
          noindex={true}
        />
        <Nav />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-cyan-400 mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-300 mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/articles"
            className="bg-cyan-500 text-gray-900 px-6 py-3 font-semibold hover:bg-cyan-400 transition-colors inline-block"
          >
            BACK TO ARTICLES
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title={article.title}
        description={article.excerpt || ""}
        ogImage={
          article.socialImageUrl ||
          article.imageUrl ||
          "https://www.recoverycompassnh.com/RecoveryCompassLogo.png"
        }
        ogImageAlt={article.title}
        ogType="article"
        canonicalUrl={`https://www.recoverycompassnh.com/articles/${
          article.slug || article.id
        }`}
        articleAuthor={article.author}
        articlePublishedTime={article.date}
        articleModifiedTime={article.dateModified || article.date}
        articleSection={getCategoryLabel(article.category)}
        breadcrumbs={[
          { name: "Home", url: "https://www.recoverycompassnh.com/" },
          {
            name: "Articles",
            url: "https://www.recoverycompassnh.com/articles",
          },
          {
            name: getCategoryLabel(article.category),
            url: `https://www.recoverycompassnh.com${getArticleCategoryUrl(
              article.category
            )}`,
          },
          {
            name: article.title,
            url: `https://www.recoverycompassnh.com/articles/${
              article.slug || article.id
            }`,
          },
        ]}
        schema={generateArticleSchema(article)}
      />

      <Nav />

      {/* Breadcrumb */}
      <div className="bg-gray-800 border-b border-gray-600">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <nav className="text-sm">
            <Link to="/articles" className="text-cyan-400 hover:underline">
              Articles
            </Link>
            <span className="text-gray-400 mx-2">→</span>
            <span className="text-gray-300">
              {getCategoryLabel(article.category)}
            </span>
            <span className="text-gray-400 mx-2">→</span>
            <span className="text-white">{article.title}</span>
          </nav>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <span
              className={`${getCategoryColor(
                article.category
              )} text-gray-900 px-3 py-1 text-sm font-bold tracking-wider rounded`}
            >
              {getCategoryLabel(article.category).toUpperCase()}
            </span>
            {article.featured && (
              <span className="bg-yellow-500 text-gray-900 px-3 py-1 text-sm font-bold tracking-wider rounded">
                FEATURED
              </span>
            )}
            {article.opinionArticle && (
              <span className="bg-orange-500 text-gray-900 px-3 py-1 text-sm font-bold tracking-wider rounded">
                OPINION
              </span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-6">
            {article.author && (
              <>
                <span className="font-semibold text-cyan-400">
                  {article.author}
                </span>
                <span>|</span>
              </>
            )}
            <time dateTime={article.date}>{formatDate(article.date)}</time>
            <span>|</span>
            <span>{article.readTime}</span>
          </div>

          {article.imageUrl && (
            <div className="mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                fetchPriority="high"
                decoding="async"
                className="w-full h-80 md:h-[500px] object-contain object-top rounded"
              />
            </div>
          )}
        </header>

        {/* Article Content */}
        <div className="mb-12">
          <div
            className="article-content prose prose-lg prose-invert max-w-none 
               prose-headings:text-cyan-400 prose-headings:font-bold
               prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
               prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
               prose-p:text-gray-300 prose-p:text-lg prose-p:leading-relaxed prose-p:mb-6
               prose-strong:text-cyan-400 prose-strong:font-semibold
               prose-a:text-cyan-400 prose-a:underline hover:prose-a:text-cyan-300
               prose-ul:mb-6 prose-ol:mb-6 prose-li:text-gray-300 prose-li:mb-2
               prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 
               prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-400"
            dangerouslySetInnerHTML={{
              __html: getFormattedArticleContent(article.content),
            }}
          />
        </div>

        {/* Article Footer */}
        <footer className="border-t border-gray-600 pt-8">
          <div className="flex justify-between items-center mb-8">
            {article.author && (
              <div>
                <p className="text-gray-400 mb-2">Written by</p>
                <p className="text-xl font-semibold text-cyan-400">
                  {article.author}
                </p>
              </div>
            )}
            <div
              className={article.author ? "text-right" : "text-center w-full"}
            >
              <p className="text-gray-400 mb-2">Published on</p>
              <time className="text-lg text-white" dateTime={article.date}>
                {formatDate(article.date)}
              </time>
              {article.dateModified &&
                article.dateModified.slice(0, 10) > article.date.slice(0, 10) && (
                  <p className="text-sm text-gray-400 mt-1">
                    Updated {formatDate(article.dateModified)}
                  </p>
                )}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/articles"
              className="bg-cyan-500 text-gray-900 px-8 py-3 font-semibold hover:bg-cyan-400 transition-colors inline-block"
            >
              BACK TO ARTICLES
            </Link>
          </div>
        </footer>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-gray-800 border-t border-gray-600">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <h2 className="text-3xl font-bold text-cyan-400 mb-8 text-center">
              RELATED ARTICLES
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/articles/${relatedArticle.slug || relatedArticle.id}`}
                  className="bg-gray-700 border border-gray-600 p-4 hover:border-cyan-500 transition-colors group"
                >
                  {relatedArticle.imageUrl && (
                    <img
                      src={relatedArticle.imageUrl}
                      alt={relatedArticle.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-40 object-cover object-top mb-3 border border-gray-600"
                    />
                  )}
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {relatedArticle.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">
                    {relatedArticle.excerpt.substring(0, 100)}...
                  </p>
                  <div className="text-xs text-gray-500">
                    {relatedArticle.author && `${relatedArticle.author} | `}
                    {relatedArticle.readTime}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <Link to={getArticleCategoryUrl("unsung-heroes")} className="hover:text-cyan-400">
                    Healthcare Heroes
                  </Link>
                </li>
                <li>
                  <Link to={getArticleCategoryUrl("stories-of-recovery")} className="hover:text-cyan-400">
                    Recovery Stories
                  </Link>
                </li>
                <li>
                  <Link to="/resources" className="hover:text-cyan-400">
                    Resources
                  </Link>
                </li>
                <li>
                  <Link to={getArticleCategoryUrl("blog-posts")} className="hover:text-cyan-400">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <Link to={getResourceCategoryUrl("Primary SUD Providers")} className="hover:text-cyan-400">
                    Treatment Centers
                  </Link>
                </li>
                <li>
                  <Link
                    to={getResourceCategoryUrl(
                      "Online Resources and Support Groups"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </Link>
                </li>
                <li>
                  <Link to={getResourceCategoryUrl("Crisis Intervention Services")} className="hover:text-cyan-400">
                    Crisis Hotline
                  </Link>
                </li>
                <li>
                  <Link to={getResourceCategoryUrl("Primary Mental Health Providers")} className="hover:text-cyan-400">
                    Mental Health
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleView;
